// Iter87 / Phase 3G — CFM Inventory Context Panel.
//
// Operator-facing UI for the in-memory CFM inventory context.
// Reads + mutates `CFMInventoryContext` (Iter87) only. Does NOT
// call any backend endpoint, does NOT fetch broker / account /
// position data, does NOT persist anything anywhere.
//
// HARD CONSTRAINTS (asserted by Iter87 tests):
//  * No `axios`. No `fetch(`. No `XMLHttpRequest`. No `useEffect`.
//  * No `localStorage` / `sessionStorage` / `IndexedDB` / cookies.
//  * No CFM / module / Wheel / Market Scanner / journal / broker /
//    account / position / order / admin endpoint references.
//  * No ranking / winner / best / preferred / auto-select language
//    outside the safety-banner whitelist ("Review before scan.").

import React, { useState } from "react";
import {
  ChevronDown, ChevronUp, ShieldAlert, Plus, Trash2, Eraser,
  Copy, Download, AlertTriangle, CheckCircle2,
} from "lucide-react";
import {
  useCFMInventory, validateRow, isSharesReady, isLeapsReady,
} from "@/contexts/CFMInventoryContext";

const TID = "cfm-inventory";

const SAFETY_LABELS = [
  { testid: `${TID}-banner-manual`,            text: "Manual inventory context only." },
  { testid: `${TID}-banner-no-broker-fetch`,   text: "No broker/account fetch." },
  { testid: `${TID}-banner-no-persistence`,    text: "No persistence." },
  { testid: `${TID}-banner-no-journal`,        text: "No journal write." },
  { testid: `${TID}-banner-no-execution`,      text: "No trade execution." },
  { testid: `${TID}-banner-review-before-scan`, text: "Review before scan." },
];

const ASSIGNMENT_POLICY_CHOICES = [
  "", "ACCEPT", "AVOID", "ROLL_FIRST", "CLOSE_FIRST", "NEUTRAL",
];

export default function CFMInventoryContextPanel() {
  const {
    rows, metadata, upsertRow, updateField, updateLeapsField,
    removeRow, clearAll, importFromJson, exportEnvelope,
  } = useCFMInventory();

  const [open, setOpen] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");
  const [importText, setImportText] = useState("");
  const [importMsg, setImportMsg] = useState(null);

  const rowList = Object.values(rows);
  const sharesReadyCount = rowList.filter(isSharesReady).length;
  const leapsReadyCount  = rowList.filter(isLeapsReady).length;

  function addRow() {
    if (!newSymbol.trim()) return;
    upsertRow(newSymbol);
    setNewSymbol("");
  }

  async function copyJson() {
    const env = exportEnvelope();
    try {
      await navigator.clipboard.writeText(JSON.stringify(env, null, 2));
    } catch (_e) { /* clipboard rejected; no backend fallback */ }
  }

  function downloadJson() {
    const env = exportEnvelope();
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const blob = new Blob([JSON.stringify(env, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `arxmentis_cfm_inventory_${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function doImport() {
    const res = importFromJson(importText);
    if (res.ok) {
      setImportMsg(`Imported ${res.count} symbol(s).`);
      setImportText("");
    } else {
      setImportMsg(`Import failed: ${res.error}`);
    }
  }

  return (
    <div
      data-testid={`${TID}-panel`}
      className="mb-5 rounded-sm border border-[#22D3CC]/30 bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid={`${TID}-toggle`}
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <ShieldAlert size={16} className="text-[#22D3CC]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          CFM Inventory Context
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3g · manual · in-memory · no broker fetch · no persistence
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* Safety banner */}
          <div
            data-testid={`${TID}-banner`}
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            {SAFETY_LABELS.map((s, i) => (
              <React.Fragment key={s.testid}>
                {i > 0 && <span className="text-[#FFB800]/50">·</span>}
                <span data-testid={s.testid}>{s.text}</span>
              </React.Fragment>
            ))}
          </div>

          {/* Metadata flags */}
          <div
            data-testid={`${TID}-metadata`}
            className="px-4 py-2 border-b border-[#26262B] text-[10px] font-mono flex items-center gap-3 flex-wrap text-[#8A8A93]"
          >
            <span data-testid={`${TID}-metadata-source`}>
              source: <span className="text-[#E0E0E0]">{metadata?.source || "operator_manual"}</span>
            </span>
            <span data-testid={`${TID}-metadata-read-only`}
                  className="px-1.5 py-0.5 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/5 text-[#00FF88]">
              read_only: true
            </span>
            <span data-testid={`${TID}-metadata-execution-enabled`}
                  className="px-1.5 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93]">
              execution_enabled: false
            </span>
            <span data-testid={`${TID}-metadata-journal-mutation-enabled`}
                  className="px-1.5 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93]">
              journal_mutation_enabled: false
            </span>
            <span data-testid={`${TID}-metadata-broker-execution-enabled`}
                  className="px-1.5 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93]">
              broker_execution_enabled: false
            </span>
            <span data-testid={`${TID}-ready-shares`}
                  className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${
                    sharesReadyCount > 0
                      ? "border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[#22D3CC]"
                      : "border-[#26262B] text-[#5F5F66]"}`}>
              {sharesReadyCount > 0
                ? `CFM Shares ready (${sharesReadyCount})`
                : "CFM Shares not ready"}
            </span>
            <span data-testid={`${TID}-ready-leaps`}
                  className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${
                    leapsReadyCount > 0
                      ? "border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7]"
                      : "border-[#26262B] text-[#5F5F66]"}`}>
              {leapsReadyCount > 0
                ? `CFM LEAPS ready (${leapsReadyCount})`
                : "CFM LEAPS not ready"}
            </span>
          </div>

          {/* Add row + bulk actions */}
          <div className="px-4 py-3 border-b border-[#26262B] flex items-center gap-2 flex-wrap">
            <input
              type="text"
              data-testid={`${TID}-new-symbol-input`}
              value={newSymbol}
              onChange={e => setNewSymbol(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === "Enter") addRow(); }}
              placeholder="SYMBOL"
              className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[10px] font-mono text-[#E0E0E0] focus:outline-none"
            />
            <button
              type="button"
              data-testid={`${TID}-add-row`}
              onClick={addRow}
              disabled={!newSymbol.trim()}
              className="px-2 py-1 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[9px] font-mono uppercase tracking-[0.12em] text-[#22D3CC] hover:bg-[#22D3CC]/20 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Plus size={10} /> Add row
            </button>
            <button
              type="button"
              data-testid={`${TID}-clear-all`}
              onClick={clearAll}
              className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={10} /> Clear all
            </button>
            <button
              type="button"
              data-testid={`${TID}-copy-json`}
              onClick={copyJson}
              className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Copy size={10} /> Copy context JSON
            </button>
            <button
              type="button"
              data-testid={`${TID}-download-json`}
              onClick={downloadJson}
              className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Download size={10} /> Download context JSON
            </button>
          </div>

          {/* Import block */}
          <details
            data-testid={`${TID}-import`}
            className="px-4 py-3 border-b border-[#26262B]"
          >
            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
              Import context from pasted JSON
            </summary>
            <div className="mt-2 space-y-2">
              <textarea
                data-testid={`${TID}-import-textarea`}
                value={importText}
                onChange={e => setImportText(e.target.value)}
                rows={4}
                spellCheck={false}
                placeholder='{"rows": {"SPY": {"shares": {...}, "leaps": {...}}}}'
                className="w-full bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[10px] font-mono text-[#E0E0E0] focus:outline-none"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`${TID}-import-apply`}
                  onClick={doImport}
                  className="px-2 py-1 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[9px] font-mono uppercase tracking-[0.12em] text-[#22D3CC] hover:bg-[#22D3CC]/20"
                >
                  Apply import
                </button>
                {importMsg && (
                  <span
                    data-testid={`${TID}-import-msg`}
                    className="text-[10px] font-mono text-[#8A8A93]"
                  >
                    {importMsg}
                  </span>
                )}
              </div>
            </div>
          </details>

          {/* Rows */}
          {rowList.length === 0 ? (
            <div
              data-testid={`${TID}-empty`}
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No inventory rows yet. Enter a symbol above and click Add row.
            </div>
          ) : (
            <ul className="divide-y divide-[#26262B]">
              {rowList.map(row => (
                <CFMInventoryRow
                  key={row.symbol}
                  row={row}
                  updateField={updateField}
                  updateLeapsField={updateLeapsField}
                  removeRow={removeRow}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CFMInventoryRow({ row, updateField, updateLeapsField, removeRow }) {
  const sym = row.symbol;
  const warnings = validateRow(row);
  const sharesReady = isSharesReady(row);
  const leapsReady  = isLeapsReady(row);

  function s(field, value) { updateField(sym, `shares.${field}`, value); }
  function l(field, value) { updateLeapsField(sym, field, value); }
  function lpos(field, value) {
    updateLeapsField(sym, `existing_leaps_position.${field}`, value);
  }

  return (
    <li
      data-testid={`${TID}-row-${sym}`}
      className="px-4 py-3 space-y-2"
    >
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
        <span
          data-testid={`${TID}-row-${sym}-symbol`}
          className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]"
        >
          {sym}
        </span>
        <span
          data-testid={`${TID}-row-${sym}-shares-ready`}
          className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${
            sharesReady
              ? "border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[#22D3CC]"
              : "border-[#26262B] text-[#5F5F66]"}`}>
          {sharesReady ? "CFM Shares ready" : "CFM Shares incomplete"}
        </span>
        <span
          data-testid={`${TID}-row-${sym}-leaps-ready`}
          className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${
            leapsReady
              ? "border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7]"
              : "border-[#26262B] text-[#5F5F66]"}`}>
          {leapsReady ? "CFM LEAPS ready" : "CFM LEAPS incomplete"}
        </span>
        <button
          type="button"
          data-testid={`${TID}-row-${sym}-remove`}
          onClick={() => removeRow(sym)}
          className="ml-auto px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40 inline-flex items-center gap-1"
        >
          <Trash2 size={10} /> Remove
        </button>
      </div>

      {/* CFM Shares fields */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-[10px] font-mono">
        <Field label="Shares owned"
               value={row.shares.shares_owned}
               onChange={v => s("shares_owned", v)}
               testid={`${TID}-row-${sym}-shares-owned`} />
        <Field label="Share basis"
               value={row.shares.share_basis}
               onChange={v => s("share_basis", v)}
               testid={`${TID}-row-${sym}-share-basis`} />
        <Field label="Total call credits"
               value={row.shares.total_call_credits}
               onChange={v => s("total_call_credits", v)}
               testid={`${TID}-row-${sym}-total-call-credits`} />
        <Field label="Min exit price"
               value={row.shares.minimum_exit_price}
               onChange={v => s("minimum_exit_price", v)}
               testid={`${TID}-row-${sym}-minimum-exit-price`} />
        <SelectField label="Assignment policy"
               value={row.shares.shares_assignment_policy}
               onChange={v => s("shares_assignment_policy", v)}
               options={ASSIGNMENT_POLICY_CHOICES}
               testid={`${TID}-row-${sym}-shares-assignment-policy`} />
        <Field label="Notes"
               value={row.shares.notes}
               onChange={v => s("notes", v)}
               testid={`${TID}-row-${sym}-notes`} />
      </div>

      {/* CFM LEAPS fields */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-[10px] font-mono">
        <label className="flex items-center gap-2 col-span-2">
          <input
            type="checkbox"
            data-testid={`${TID}-row-${sym}-allow-leaps`}
            checked={!!row.leaps.allow_leaps}
            onChange={e => l("allow_leaps", e.target.checked)}
          />
          <span className="text-[#8A8A93] uppercase tracking-[0.12em]">
            allow LEAPS / PMCC
          </span>
        </label>
        <Field label="Total short-call credits"
               value={row.leaps.total_short_call_credits}
               onChange={v => l("total_short_call_credits", v)}
               testid={`${TID}-row-${sym}-total-short-call-credits`} />
        <SelectField label="LEAPS assignment policy"
               value={row.leaps.leaps_assignment_policy}
               onChange={v => l("leaps_assignment_policy", v)}
               options={ASSIGNMENT_POLICY_CHOICES}
               testid={`${TID}-row-${sym}-leaps-assignment-policy`} />
        <Field label="Campaign notes"
               value={row.leaps.campaign_notes}
               onChange={v => l("campaign_notes", v)}
               testid={`${TID}-row-${sym}-campaign-notes`} />
      </div>

      {row.leaps.allow_leaps && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-[10px] font-mono">
          <Field label="Underlying"
                 value={row.leaps.existing_leaps_position.underlying_symbol}
                 onChange={v => lpos("underlying_symbol", v)}
                 testid={`${TID}-row-${sym}-leaps-underlying`} />
          <Field label="Expiration"
                 value={row.leaps.existing_leaps_position.expiration}
                 onChange={v => lpos("expiration", v)}
                 testid={`${TID}-row-${sym}-leaps-expiration`} />
          <Field label="Strike"
                 value={row.leaps.existing_leaps_position.strike}
                 onChange={v => lpos("strike", v)}
                 testid={`${TID}-row-${sym}-leaps-strike`} />
          <SelectField label="Option type"
                 value={row.leaps.existing_leaps_position.option_type}
                 onChange={v => lpos("option_type", v)}
                 options={["call", "put"]}
                 testid={`${TID}-row-${sym}-leaps-option-type`} />
          <Field label="Quantity"
                 value={row.leaps.existing_leaps_position.quantity}
                 onChange={v => lpos("quantity", v)}
                 testid={`${TID}-row-${sym}-leaps-quantity`} />
          <Field label="Basis"
                 value={row.leaps.existing_leaps_position.basis}
                 onChange={v => lpos("basis", v)}
                 testid={`${TID}-row-${sym}-leaps-basis`} />
          <Field label="Delta"
                 value={row.leaps.existing_leaps_position.delta}
                 onChange={v => lpos("delta", v)}
                 testid={`${TID}-row-${sym}-leaps-delta`} />
          <Field label="Theta"
                 value={row.leaps.existing_leaps_position.theta}
                 onChange={v => lpos("theta", v)}
                 testid={`${TID}-row-${sym}-leaps-theta`} />
        </div>
      )}

      {warnings.length > 0 && (
        <div
          data-testid={`${TID}-row-${sym}-warnings`}
          className="text-[10px] font-mono text-[#FFB800] space-y-0.5"
        >
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
      {sharesReady && (
        <div
          data-testid={`${TID}-row-${sym}-shares-ready-note`}
          className="text-[10px] font-mono text-[#22D3CC] flex items-center gap-1"
        >
          <CheckCircle2 size={10} /> CFM Shares context complete.
        </div>
      )}
      {leapsReady && (
        <div
          data-testid={`${TID}-row-${sym}-leaps-ready-note`}
          className="text-[10px] font-mono text-[#A855F7] flex items-center gap-1"
        >
          <CheckCircle2 size={10} /> CFM LEAPS context complete.
        </div>
      )}
    </li>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">{label}</span>
      <input
        type="text"
        data-testid={testid}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">{label}</span>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0]"
      >
        {options.map(opt => (
          <option key={opt || "_blank"} value={opt}>{opt || "—"}</option>
        ))}
      </select>
    </label>
  );
}
